// REACT IMPORTS
import { useMemo } from "react";
import { PayloadAction, createAsyncThunk, createSlice } from "@reduxjs/toolkit";

// COMPONENT IMPORTS
import { useAppSelector } from "store/hooks";
import { RootState } from "store/store";
import {
  createCard,
  createStaxProfile,
  createWalletsProfile,
  deleteOrDefaultCard,
  getCardDetails,
  getCountryList,
  getOldPaymentMethodsData,
} from "store/services/paymentMethodServices";
import {
  formatBanks,
  formatCards,
  formatWallets,
} from "utils/HelperFunctions";
import {
  getCardExpiryFlags,
  isCardExpiryUsable,
} from "utils/cardExpiration";

interface InitialState {
  oldCardData: any;
  oldBankData: any;
  countryList: any;
  cardList: any;
  bankList: any;
  isDataLoading: boolean;
  hasLoadedPaymentMethods: boolean;
  walletList: any;
  paymentMethodLists: any;
}

const initialState: InitialState = {
  isDataLoading: false,
  hasLoadedPaymentMethods: false,
  oldCardData: [],
  oldBankData: [],
  countryList: [],
  cardList: [],
  bankList: [],
  walletList: [],
  paymentMethodLists: [],
};

export const oldPaymentMethodsGet = createAsyncThunk(
  "old-payment-method-data/get",
  async (params: any) => {
    return await getOldPaymentMethodsData(params);
  }
);

export const cardDeleteOrDefault = createAsyncThunk(
  "card-default/delete",
  async (payload: any) => {
    return await deleteOrDefaultCard(payload);
  }
);

export const countryListGet = createAsyncThunk("country-list/get", async () => {
  return await getCountryList();
});

export const addCard = createAsyncThunk("card/add", async (payload: any) => {
  return await createCard(payload);
});

export const walletsProfile = createAsyncThunk(
  "wallets-profile/create",
  async (payload: any) => {
    return await createWalletsProfile(payload);
  }
);

export const staxProfile = createAsyncThunk(
  "stax-profile/create",
  async (payload: any) => {
    return await createStaxProfile(payload);
  }
);

export const cardDetailsGet = createAsyncThunk(
  "card-details/get",
  async (params: any) => {
    return await getCardDetails(params);
  }
);

const prepareAllPaymentMethodData = (data: any) => {
  let paymentMethod = data?.length ? data : [];
  paymentMethod = paymentMethod.filter((x: any) => {
    if (!x.available) return false;
    if (x.payment_method === "CC") {
      return isCardExpiryUsable(x?.cc_expiry);
    }
    if (x.payment_method === "ACH") return x.is_connected === "Y";
    return true;
  });

  paymentMethod = paymentMethod.sort((a: any, b: any) => {
    const aDefault = a.default_profile === "Y" ? 0 : 1;
    const bDefault = b.default_profile === "Y" ? 0 : 1;
    if (aDefault !== bDefault) return aDefault - bDefault;
    return 0;
  });

  return paymentMethod;
};

const prepareData = (data: any) => {
  let cardData = data?.length ? data : [];

  let ndata = cardData?.filter((x: any) => x.payment_method === "CC");

  let fData = ndata?.map((x: any) => {
    return {
      ...x,
      ...getCardExpiryFlags(x?.cc_expiry),
    };
  });

  fData.sort((a: any, b: any) => {
    if (a.available === b.available) {
      const aDefault = a.default_profile === "Y" ? 0 : 1;
      const bDefault = b.default_profile === "Y" ? 0 : 1;
      if (aDefault !== bDefault) return aDefault - bDefault;
      return 0;
    }
    return a.available ? -1 : 1;
  });

  return fData || [];
};

const prepareBankData = (data: any) => {
  let bankData = data?.length ? data : [];
  let fData = bankData?.filter((x: any) => x.payment_method === "ACH");
  fData.sort((a: any, b: any) => {
    if (a.available === b.available) {
      const aDefault = a.default_profile === "Y" ? 0 : 1;
      const bDefault = b.default_profile === "Y" ? 0 : 1;
      if (aDefault !== bDefault) return aDefault - bDefault;
      return 0;
    }
    return a.available ? -1 : 1;
  });

  return fData || [];
};

const prepareWalletData = (data: any) => {
  let walletData = data?.length ? data : [];

  walletData.sort((a: any, b: any) => {
    if (a.available === b.available) {
      const aDefault = a.default_profile === "Y" ? 0 : 1;
      const bDefault = b.default_profile === "Y" ? 0 : 1;
      if (aDefault !== bDefault) return aDefault - bDefault;
      return 0;
    }
    return a.available ? -1 : 1;
  });

  return walletData || [];
};
/* ------------------------------------- PAYMNET METHOD SLICE ------------------------------------- */

const paymentMethodSlice = createSlice({
  name: "paymentMethod",
  initialState,
  reducers: {
    clearPaymentMethodSlice: (state) => {
      for (const key in initialState) {
        if (initialState.hasOwnProperty(key)) {
          const typedKey = key as keyof typeof initialState;
          state[typedKey] = initialState[typedKey];
        }
      }
    },
    updateWalletList: (state, action) => {
      state.walletList = action.payload;
    },
    updateBankList: (state, action) => {
      state.bankList = prepareBankData(action.payload);
    },
    updateCardList: (state, action) => {
      state.cardList = prepareData(action.payload);
    },
  },
  extraReducers: (builder) => {
    builder.addCase(
      countryListGet.fulfilled,
      (state, action: PayloadAction<any>) => {
        let ndata = action.payload?.body?.data || [];
        state.countryList = ndata?.map((x: any) => {
          return {
            name: x.country_name,
            value: x.country_code,
          };
        });
      }
    );

    builder.addCase(oldPaymentMethodsGet.pending, (state, action) => {
      state.isDataLoading = true;
      state.hasLoadedPaymentMethods = false;
    });

    builder.addCase(oldPaymentMethodsGet.rejected, (state) => {
      state.isDataLoading = false;
      state.hasLoadedPaymentMethods = true;
    });

    builder.addCase(
      walletsProfile.fulfilled,
      (state, action: PayloadAction<any>) => {
        if (action.payload?.body.success) {
          if (
            action.payload.body?.data?.paymentMethod?.payment_method === "CC"
          ) {
            state.cardList = prepareData([
              ...state.cardList,
              { ...action.payload?.body?.data?.paymentMethod, available: true },
            ]);
          } else if (
            action.payload.body?.data?.paymentMethod?.payment_method === "ACH"
          ) {
            state.bankList = prepareData([
              ...state.bankList,
              { ...action.payload?.body?.data?.paymentMethod, available: true },
            ]);
          } else {
            state.walletList = prepareWalletData([
              ...state.walletList,
              { ...action.payload?.body?.data?.paymentMethod, available: true },
            ]);
          }
        }
      }
    );

    builder.addCase(
      oldPaymentMethodsGet.fulfilled,
      (state, action: PayloadAction<any>) => {
        state.isDataLoading = false;
        state.hasLoadedPaymentMethods = true;
        const formateCardList: any = formatCards(action.payload?.body?.data);
        const formateBankList = formatBanks(action.payload?.body?.data);
        const formateWalletList = formatWallets(action.payload?.body?.data);

        state.cardList = prepareData(formateCardList) || [];
        state.bankList = prepareBankData(formateBankList) || [];
        state.walletList = prepareWalletData(formateWalletList) || [];

        state.paymentMethodLists = prepareAllPaymentMethodData(
          action.payload?.body?.data
        );
      }
    );
  },
});

const {
  clearPaymentMethodSlice,
  updateWalletList,
  updateCardList,
  updateBankList,
} = paymentMethodSlice.actions;
export {
  clearPaymentMethodSlice,
  updateWalletList,
  updateCardList,
  updateBankList,
};
export default paymentMethodSlice.reducer;

const selectCountryList = (state: RootState) => state.paymentMethod.countryList;
export const useCountryList = () => {
  const countryList = useAppSelector(selectCountryList);
  return useMemo(() => countryList, [countryList]);
};

const selectBankData = (state: RootState) => state.paymentMethod.bankList;
export const useBankData = () => {
  const bankList = useAppSelector(selectBankData);
  return useMemo(() => bankList, [bankList]);
};

const selectWalletData = (state: RootState) => state.paymentMethod.walletList;
export const useOldWalletData = () => {
  const walletList = useAppSelector(selectWalletData);
  return useMemo(() => walletList, [walletList]);
};

const selectCardData = (state: RootState) => state.paymentMethod.cardList;
export const useCardData = () => {
  const cardList = useAppSelector(selectCardData);
  return useMemo(() => cardList, [cardList]);
};

const selectPaymentMethodData = (state: RootState) =>
  state.paymentMethod.paymentMethodLists;
export const usePaymentMethodData = () => {
  const paymentMethodLists = useAppSelector(selectPaymentMethodData);
  return useMemo(() => paymentMethodLists, [paymentMethodLists]);
};

const selectLoading = (state: RootState) => state.paymentMethod.isDataLoading;
export const useIsDataLoading = () => {
  const isDataLoading = useAppSelector(selectLoading);
  return useMemo(() => isDataLoading, [isDataLoading]);
};

const selectHasLoadedPaymentMethods = (state: RootState) =>
  state.paymentMethod.hasLoadedPaymentMethods;
export const useHasLoadedPaymentMethods = () => {
  const hasLoadedPaymentMethods = useAppSelector(selectHasLoadedPaymentMethods);
  return useMemo(() => hasLoadedPaymentMethods, [hasLoadedPaymentMethods]);
};
